import React, { useEffect, useState } from "react";
import { QrCode, Copy, MessageCircle, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { useApp, formatBRL } from "../context/AppContext";
import { useToast } from "../hooks/use-toast";

export const PixFaturaDialog = ({ fatura, residente, open, onClose }) => {
  const { obterPixFatura, enviarPixFatura } = useApp();
  const { toast } = useToast();
  const [pix, setPix] = useState(null);
  const [erro, setErro] = useState("");
  const [copiado, setCopiado] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open || !fatura) return;
    setPix(null); setErro(""); setCopiado(false);
    obterPixFatura(fatura.residenteId, fatura.mes)
      .then(setPix)
      .catch((e) => setErro(e?.response?.data?.detail || "Não foi possível gerar o Pix."));
  }, [open, fatura?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const copiar = async () => {
    try { await navigator.clipboard.writeText(pix.payload); setCopiado(true); setTimeout(() => setCopiado(false), 2500); }
    catch { toast({ title: "Não foi possível copiar", variant: "destructive" }); }
  };
  const enviar = async () => {
    setEnviando(true);
    try {
      const r = await enviarPixFatura(fatura.residenteId, fatura.mes);
      toast({ title: r.status === "enviado" ? `Fatura com Pix enviada para ${r.nome}` : `Envio: ${r.status}`, description: r.telefone });
    } catch (e) {
      toast({ title: "Falha ao enviar", description: e?.response?.data?.detail || "Erro", variant: "destructive" });
    } finally { setEnviando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pix-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5 text-emerald-600" /> Pix da fatura · {residente?.nome}</DialogTitle>
        </DialogHeader>
        {erro && <p className="text-sm text-red-600" data-testid="pix-erro">{erro}</p>}
        {!pix && !erro && <p className="text-sm text-slate-500">Gerando QR Code…</p>}
        {pix && (
          <div className="space-y-4">
            <div className="flex gap-4 items-start">
              <img src={`data:image/png;base64,${pix.qrPngBase64}`} alt="QR Code Pix" className="w-44 h-44 rounded-lg border border-slate-200 bg-white" data-testid="pix-qr-img" />
              <div className="text-sm space-y-1.5 flex-1">
                <p className="text-2xl font-bold text-slate-800" data-testid="pix-valor">{formatBRL(pix.valor)}</p>
                <p className="text-slate-600">Chave Pix (CNPJ): <b data-testid="pix-chave">{pix.chave_fmt}</b></p>
                <p className="text-slate-600">Favorecido: <b>{pix.nome}</b></p>
                <p className="text-xs text-slate-400">Referência {fatura.mes} · identificador {pix.txid}</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Pix copia e cola</p>
              <div className="flex gap-2">
                <textarea readOnly value={pix.payload} rows={3} className="flex-1 text-[11px] font-mono rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 resize-none" data-testid="pix-payload" />
                <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={copiar} data-testid="pix-copiar">
                  {copiado ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />} {copiado ? "Copiado" : "Copiar"}
                </Button>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={onClose} data-testid="pix-fechar">Fechar</Button>
              <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={enviando} onClick={enviar} data-testid="pix-enviar-whatsapp">
                <MessageCircle className="w-4 h-4" /> {enviando ? "Enviando..." : "Enviar fatura com Pix (WhatsApp)"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
